// Quantity boxes follow the shop's live stock (snippets/quantity-input,
// data-stock-variant): Liquid's inventory_quantity can lag a stock change by
// half an hour (26 Oct 2026: English Breakfast pouch set 1 -> 50, the box
// stopped at 1 until 06:30), while the Storefront API's quantityAvailable is
// right at once. The page's number stays until the answer arrives, and stays
// if it never does. Every region cardo swaps in (a size change, the basket
// drawn again) sends ui:hydrate, so its boxes are asked about again.
// The sold-out look follows the same number (Michael 2026-10-26): every
// [data-stock-variant] gets data-sold-out when none are left and loses it when
// some are, and the snippets show "Sold out", the tint and the band from that.
// [data-stock-disable] is disabled and [data-stock-aria] aria-disabled with it.
// Each answer is then sent as stock:live on the document (deal-picker.js).
const token = document.documentElement.dataset.storefrontToken
const query = `query ($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { id quantityAvailable } } }`

const queued = new Set()
let timer = 0
let pageQueued = false // the page's own boxes are in the next request
let ready
// Settles once the page's first answer is in, or failed, or nothing was asked
// (deal-picker.js waits on it before a ?trio= link adds the trio's tins).
export const stockReady = new Promise((resolve) => (ready = resolve))

const ask = async (ids) => {
  const res = await fetch('/api/2026-01/graphql.json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
    body: JSON.stringify({ query, variables: { ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`) } }),
  })
  const { data } = await res.json()
  const stock = new Map()
  for (const node of data?.nodes || []) {
    if (node && typeof node.quantityAvailable === 'number')
      stock.set(node.id.split('/').pop(), Math.max(0, node.quantityAvailable))
  }
  return stock
}

const apply = (stock) => {
  document.querySelectorAll('[data-stock-variant]').forEach((box) => {
    const n = stock.get(box.dataset.stockVariant)
    if (n === undefined) return
    const out = n < 1
    // Only when it changes: a button held off while its add is sent stays off.
    const changed = box.hasAttribute('data-sold-out') !== out
    box.toggleAttribute('data-sold-out', out)
    if (changed && box.hasAttribute('data-stock-disable')) box.disabled = out
    if (box.hasAttribute('data-stock-aria')) out ? box.setAttribute('aria-disabled', 'true') : box.removeAttribute('aria-disabled')
    if (!box.hasAttribute('data-qty')) return
    const input = box.querySelector('input[type="number"]')
    const line = input?.hasAttribute('data-cardo-cart-qty')
    // A basket box was cut to the page's number when it was drawn; its value
    // attribute still holds what's in the basket, so start from that again -
    // only if it still stands at the cut, so a tap meanwhile isn't undone (review 2026-10-26).
    if (line && +input.value === +box.dataset.max && +input.defaultValue > +input.value) input.value = input.defaultValue
    const before = input?.value
    box.dataset.max = n
    if (input) input.max = n
    box.dispatchEvent(new CustomEvent('qty:sync', { bubbles: true }))
    // The price and totals beside a box (product-price.js, add-ons.js) hear a
    // lowered number. A basket line is left as it is, as the page's number did.
    if (input && !line && input.value !== before)
      input.dispatchEvent(new Event('change', { bubbles: true }))
  })
}

// A product page opens on another size when its first looked sold out
// (varient-cards, data-stock-default). If the live stock has it, the page
// switches to it, unless the shopper has already started choosing.
let touched = false
const touch = () => (touched = true)
document.addEventListener('pointerdown', touch, { capture: true, once: true })
document.addEventListener('keydown', touch, { capture: true, once: true })

const run = async () => {
  timer = 0
  const ids = [...queued]
  const forPage = pageQueued
  queued.clear()
  pageQueued = false
  try {
    apply(await ask(ids))
    document.dispatchEvent(new CustomEvent('stock:live'))
    if (forPage) {
      const first = document.querySelector('[data-stock-default]:not([data-sold-out]):not([aria-current="true"])')
      document.querySelectorAll('[data-stock-default]').forEach((el) => el.removeAttribute('data-stock-default'))
      if (first && !touched) first.click()
    }
  } catch {}
  if (forPage) ready()
}

if (token) {
  document.addEventListener('ui:hydrate', (e) => {
    const root = e.detail?.target || document
    const boxes = [...(root.querySelectorAll?.('[data-stock-variant]') || [])]
    if (root.matches?.('[data-stock-variant]')) boxes.push(root)
    boxes.forEach((box) => queued.add(box.dataset.stockVariant))
    // Regions swapped in together (the side basket and the basket page) share one request.
    if (queued.size) timer ||= setTimeout(run)
    // Only the page's own answer settles stockReady, not a basket redrawn meanwhile.
    if (root === document) boxes.length ? (pageQueued = true) : ready()
  })
} else ready()
